import React from "react";
import ReactGA from "react-ga";
import _ from "lodash";
import { getCurrentPage } from "../helpers";
import { Link } from "react-router";
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import { history } from "../store";

import Rides from "./Rides";
import Footer from "./Footer";
import Seo from "./Seo";

class LeisureRides extends React.Component {
	state = {
		loading: false,
		rides: []
	};

	handleClearFilters = (e) => {
		e.preventDefault()
		this.props.setFilter('searchName', 'none');
		['distance', 'difficulty', 'type'].forEach(filter => this.props.setFilter(filter, 0))
		history.push({ pathname: `/leisure-rides/` });
	}

	handleFilter = (e) => {
		const { name, value } = e.target;

		// GA Track
		ReactGA.event({
			category: _.startCase(getCurrentPage()),
			action: "Filter",
			label: name,
		});

		// Update filters and call updateRides
		this.props.setFilter(name, value);

		history.push({
			pathname: `/leisure-rides`,
			query: { ...this.props.location.query, [name]: value },
		});

		this.props.scrollSidebarTo(272);
	};

	handleSingle = (ridename = this.props.params.ridename) => {
		// '/' was converted to '_' for URL
		const rideName = ridename.replace("_", "/");

		const name = "routeName";
		const value = rideName;

		// Update filters and call updateRides
		this.props.setFilter(name, value);

		// Scroll to top
		this.props.scrollSidebarTo(0);
	};

	handlePolyClick = (rideName) => {
		const ride = encodeURI(rideName.replace("/", "_"));

		// GA Track
		ReactGA.event({
			category: _.startCase(getCurrentPage()),
			action: "Show Ride - Map",
			label: rideName,
		});

		history.push({
			pathname: `/leisure-rides/${ ride }`,
			query: this.props.location.query,
		});
	};

	updateRides = () => {
		// Set up an array of rides to render
		const rides = [];
		const filteredRides = [];

		// Create an array to store polyline data to pass to the map
		const routes = [];

		// Get the data we'll need to show rides and plot polylines (without the gotData boolean)
		const data = { ...this.props.leisureRides };
		delete data["gotData"];

		// Loop through all rides and decide which ones to output based on filter selections
		Object.keys(data).forEach((i) => {
			const ride = data[i];
			const distance = parseFloat(this.props.filters.distance);
			const difficulty = parseInt(this.props.filters.difficulty, 10);
			const type = parseInt(this.props.filters.type, 10);

			/*
			 * If the ride matches the current filter selections, push it to the rides array
			 * First check, difficulty and type filters. Then check if value is over 15 miles or under.
			 */
			if (
				(parseFloat(ride.difficulty) === difficulty || difficulty === 0) &&
				(parseFloat(ride.type) === type || type === 0) &&
				(ride.name === this.props.filters.routeName ||
					!this.props.filters.routeName)
			) {
				if (distance > 15 && parseFloat(ride.distance) > 15) {
					rides.push(ride);
				} else if (
					(distance !== 15.1 && parseFloat(ride.distance) < distance) ||
					distance === 0
				) {
					rides.push(ride);
				}
			}
		});

		rides.forEach((ride) => {
			// Check if there is a searchName to match from filters.
			const { searchName } = this.props.filters
			if (searchName && searchName !== 'none') {
				let filter = this.props.filters.searchName.toUpperCase();
				let routeName = ride.name.toUpperCase();

				if (routeName.indexOf(filter) > -1) {
					filteredRides.push(ride);
				}
			} else {
				// If no search being input add them all to filteredRides.
				filteredRides.push(ride);
			}
		});

		// Now that we have an array of rides to show, draw polylines only for those
		filteredRides.forEach((ride) => {
			// Routes can have multiple path parts
			ride.paths.forEach((part) => {
				// Get all lat/lng
				const path = part.map((coords) => ({ lat: coords[1], lng: coords[0] }));

				// Add the route to the routes array
				routes.push({
					color: `#${ ride.color }`,
					path,
					onClick: this.handlePolyClick,
					rideName: ride.name,
				});
			});
		});

		this.props.addPolylines("routes", routes);

		// Store the rides to be shown in component state
		this.setState({ rides: filteredRides });
	};

	componentDidUpdate(prevProps) {
		// Leisure Rides have loaded
		if (this.props.leisureRides !== prevProps.leisureRides) {
			this.setState({
				loading: false,
			});

			this.updateRides();
		}

		// New Ride selected or clears
		if (this.props.params.ridename !== prevProps.params.ridename) {
			if (this.props.params.ridename) {
				this.handleSingle();
			} else {
				this.props.setFilter("routeName", null);
			}
		}

		// New filters
		const newFilters = !_.isEqual(this.props.filters, prevProps.filters);
		if (newFilters) {
			this.updateRides();
		}
	}

	componentDidMount() {
		if (_.size(this.props.leisureRides) === 0) {
			this.setState({
				loading: true,
			});

			this.props.getRides();
		} else {
			this.updateRides();
		}

		// Ride selected
		if (this.props.params.ridename) {
			this.handleSingle();
		}

		const query = this.props.router.location.query;

		const queryMapped = Object.keys(query).map((key, value) => [
			key,
			query[key],
		]);
		const queryNo = Object.keys(query).length;

		for (let i = 0; i < queryNo; i++) {
			const filterName = queryMapped[i][0];
			const filterValue = queryMapped[i][1];

			this.props.setFilter(filterName, filterValue);
		}
	}

	componentWillUnmount() {
		this.props.clearMap("routes");
		this.props.clearMap("poly");
		this.props.removeOverlay("sitewide");
		this.props.clearPOI();
		this.props.setFilter("routeName", null);
	}

	title = () => {
		const rideName = this.props.params.ridename;

		if (rideName) {
			return `${ rideName } leisure ride`;
		}

		return "Leisure cycle rides suitable for all in the West of England";
	};

	desc = () => {
		return "There are lots of wonderful cycle trails in the West of England, including the popular Bristol to Bath Railway Path and mountain biking at Ashton Court Estate, Bristol.";
	};

	render() {
		const wrapperClasses = this.state.loading
			? "leisure-rides-wrapper sidebar-content-vertical-container loading"
			: "leisure-rides-wrapper sidebar-content-vertical-container";

		// Single ride or multiple
		const singleRide = this.props.params.ridename;

		const allRides = Object.values(this.props.leisureRides).filter(ride => ride.name)

		return (
			<div>
				<Seo title={ this.title() } desc={ this.desc() } />

				<CSSTransitionGroup
					transitionName="slide-fade"
					transitionAppear={ true }
					transitionAppearTimeout={ 400 }
					transitionEnterTimeout={ 400 }
					transitionLeaveTimeout={ 400 }
				>
					<div className={ wrapperClasses }>
						<CSSTransitionGroup
							transitionName="rides-header"
							transitionEnterTimeout={ 400 }
							transitionLeaveTimeout={ 400 }
						>
							{ !singleRide && (
								<div className="rides-header-wrapper">
									<RidesHeader />

									<RidesFilter
										rides={ allRides }
										filters={ this.props.filters }
										handleClearFilters={ this.handleClearFilters }
										handleFilter={ this.handleFilter }
										rideNo={ this.state.rides.length }
									/>
								</div>
							) }
						</CSSTransitionGroup>

						{ singleRide && <BackToFilter /> }

						{ this.props.leisureRides.gotData && (
							<Rides
								handleFilter={ this.handleFilter }
								rides={ this.state.rides }
								filters={ this.props.filters }
								singleRide={ singleRide }
							/>
						) }

						<Footer />
					</div>
				</CSSTransitionGroup>
			</div>
		);
	}
}

const RidesHeader = () => (
	<header className="rides-header">
		<h1 className="main-title">Leisure Rides</h1>
		<p>
			There are lots of wonderful cycle trails in the West of England, including
			the famous Bristol to Bath Railway Path and mountain biking at Ashton
			Court Estate, Bristol.
		</p>

		<span className="rides-loading">Loading Leisure Rides...</span>
	</header>
);

class BackToFilter extends React.Component {
	trackClick = (query) => {
		// GA Track
		ReactGA.event({
			category: _.startCase(getCurrentPage()),
			action: "Back to rides list",
		});
	};

	componentDidMount() {
		this.focusOn();
	}

	focusOn() {
		setTimeout(() => {
			const ref = this.refs.backBtn;
			const className = ref && ref.props.className;
			const selectedBtn = document.querySelector(`.${ className }`);
			selectedBtn && selectedBtn.focus();
		}, 1000);
	}

	render() {
		return (
			<Link
				to="/leisure-rides"
				className="back-button"
				onClick={ (e) => this.trackClick() }
				ref="backBtn"
				tabIndex="0"
			>
				<svg className="icon" aria-hidden="true">
					<use xlinkHref="#arrow_carrot-left" />
				</svg>
				<span>Back to list of rides</span>
			</Link>
		);
	}
}

const RidesFilter = ({ rides, rideNo, filters, handleFilter, handleClearFilters }) => {
	const { searchName } = filters
	return (
		<form
			className="ride-filters-form sidebar-divider"
			aria-label="Leisure ride filters"
		>
			<h2 className="divider-title">
				<span className="assistive-text">Leisure</span>
				{ rideNo } { rideNo === 1 ? "Ride" : "Rides" }
				<span className="assistive-text">filters</span>
			</h2>

			<svg className="tab-illustration">
				<use xlinkHref="#ill-leisure-rides" />
			</svg>

			<fieldset>
				<legend className="ride-filters-form__legend">
					Leisure ride filters
				</legend>

				<button
					className="clear-filters"
					onClick={ handleClearFilters }
					aria-label="clear filters"
				>Clear all</button>

				<label htmlFor="ride-name" className="input-label">Select a leisure ride</label>
				<select
					name="searchName"
					id="searchName"
					className="filter-distance ride-filters__select--fw"
					onChange={ handleFilter }
					value={ searchName || undefined }
				>
					<option
						value="none"
					>Find a route</option>

					{ rides.map((item, i) => (
						<option
							key={ i }
							value={ item.name }
						>{ item.name }</option>
					)) }
				</select>

				<div className="select-wrapper">
					<div className="select-container">
						<label htmlFor="distance" className="input-label">
							Select a distance
						</label>
						<select
							name="distance"
							value={ filters.distance }
							id="distance"
							className="filter-distance"
							onChange={ handleFilter }
						>
							<option value="0">Distance</option>
							<option value="5">Less than 5 miles</option>
							<option value="10">Less than 10 miles</option>
							<option value="15">Less than 15 miles</option>
							<option value="15.1">More than 15 miles</option>
						</select>
					</div>

					<div className="select-container">
						<label htmlFor="difficulty" className="input-label">
							Select a difficulty
						</label>
						<select
							name="difficulty"
							value={ filters.difficulty }
							id="difficulty"
							className="filter-difficulty"
							onChange={ handleFilter }
						>
							<option value="0">Difficulty</option>
							<option value="1">Easy</option>
							<option value="2">Moderate</option>
							<option value="3">Challenging</option>
						</select>
					</div>
				</div>

				<label htmlFor="type" className="input-label">
					Select a type of ride
				</label>
				<select
					name="type"
					value={ filters.type }
					id="type"
					className="filter-type"
					onChange={ handleFilter }
				>
					<option value="0">Type</option>
					<option value="1">Leisure ride</option>
					<option value="3">Mountain bike trail</option>
				</select>
			</fieldset>
		</form>
	);
};

export default LeisureRides;



// WEBPACK FOOTER //
// ./src/components/LeisureRides.js